/**
 * Document Store Types
 * Based on the Document Store API documentation
 */

// ==========================================
// ENUMS
// ==========================================

export enum DocumentProgressStatusEnum {
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  FAILED = 'FAILED'
}

export type OverallStatus = 'NONE' | 'IN_PROGRESS' | 'DONE' | 'FAILED';

export type SupportedFileType = 'pdf' | 'docx' | 'txt' | 'csv' | 'jpg' | 'jpeg' | 'png' | 'gif' | 'mp4' | 'avi' | 'mov' | 'mp3' | 'wav' | 'm4a';

// ==========================================
// API RESPONSE WRAPPERS
// ==========================================

export interface APIResponse<T> {
  success: boolean;
  code: number;
  description: string;
  data: T;
  statusCode?: number;
  headers?: {
    'Last-Modified'?: string;
    'Cache-Control'?: string;
  };
}

export interface PaginatedResponse<T> {
  success: boolean;
  code: number;
  description: string;
  data: T[];
  meta_data: {
    total_records: number;
    current_page: number;
    records_per_page: string | number;
  };
  statusCode?: number;
  headers?: {
    'Last-Modified'?: string;
    'Cache-Control'?: string;
  };
}

export interface ErrorResponse {
  error: string;
  error_description: string;
  error_code?: string;
  http_code: number;
  statusCode: number;
  validation_errors?: Array<{
    property: string;
    message: string;
    value: any;
  }>;
}

// ==========================================
// DOCUMENT ENTITIES
// ==========================================

export interface DocumentUrl {
  _id: string;
  name: string;
  key: string;
  url: string;
  fileType: string;
  status: DocumentProgressStatusEnum;
  remarks?: string; // failure reason when status is FAILED (e.g. "You are out of credits…")
  fileSize: number;
  useAsExample: boolean;
  uploadedByUserId?: string;
  uploadedBy?: string;
  /**
   * AI-generated summary of the document's content. Written by the backend onto
   * document_urls.$.summary once processing reaches the summary step, so it is
   * absent until status is DONE (and stays absent if summarisation failed).
   */
  summary?: string;
  /** Extra metadata extracted alongside the summary. */
  metadata?: {
    title?: string;
    tags?: string[];
    word_count?: number;
    page_count?: number;
    processing_date?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface DocumentStore {
  _id: string;
  id: string;
  name: string;
  description: string;
  document_urls: DocumentUrl[];
  status: OverallStatus;
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

// ==========================================
// REQUEST TYPES
// ==========================================

export interface CreateDocumentStoreRequest {
  name: string;
  description: string;
}

export interface UpdateDocumentStoreRequest {
  name?: string;
  description?: string;
  document_urls?: DocumentUrlInput[];
}

export interface DocumentUrlInput {
  _id?: string; // Include for existing documents so backend can match and preserve DB-owned fields
  name: string;
  key: string;
  url: string;
  fileType: string;
  status?: DocumentProgressStatusEnum;
  useAsExample?: boolean;
  fileSize?: number;
  // uploadedByUserId is automatically set from currentContext.user.sub on backend
}

export interface ListDocumentStoresRequest {
  skip?: number;
  limit?: number;
  name?: string;
}

export interface SearchDocumentContentRequest {
  search_query: string;
  knowledge_base?: string; // optional — search resolves by document_store_id
  document_store_id: string; // _id of the document store
  top_k?: number;
  include_scores?: boolean;
  enable_reranking?: boolean;
  enable_context_expansion?: boolean;
}

// ==========================================
// RESPONSE TYPES
// ==========================================

export interface DocumentStoreResponse extends APIResponse<DocumentStore> {}

export interface DocumentStoresListResponse extends PaginatedResponse<DocumentStore> {}

export interface DeleteDocumentStoreResponse extends APIResponse<{ message: string }> {}

export interface SearchDocumentContentResponse extends APIResponse<{ 
  content: string;
  raw_data?: string[]; // Array of raw content chunks
  scores?: number[]; // Similarity scores (if include_scores=true)
  chunks_count?: number; // Total number of chunks found
}> {}

/**
 * Enhanced search result with all data
 */
export interface SearchResult {
  content: string;
  raw_data: string[];
  scores?: number[];
  chunks_count: number;
}

export interface DropdownListResponse extends APIResponse<{
  saved_documents: Array<{
    _id: string;
    name: string;
  }>;
}> {}

// ==========================================
// COURSE STRUCTURE ANALYSIS
// ==========================================

export type CourseDeliveryType =
  | 'html'
  | 'html_with_audio'
  | 'video_presentation'
  | 'avatar_video';

/** How much of a course a topic deserves. Only the counts per level are returned. */
export type TopicDepth = 'major' | 'supporting' | 'detail';

/** The ceilings the suggestion was clamped to, echoed back so the UI never hardcodes them. */
export interface DeliveryLimits {
  max_modules: number;
  max_content_items_per_module: number;
  min_scenes_per_content: number;
  max_scenes_per_content: number;
  min_duration_seconds: number;
  max_duration_seconds: number;
}

/**
 * The structure range the material supports.
 *
 * Scene fields appear only for video_presentation and duration fields only for avatar_video —
 * avatar courses have no scenes, they have a video length per content item. The two are
 * alternatives, never both, and html/html_with_audio have neither.
 */
export interface SuggestedStructureRange {
  min_modules: number;
  max_modules: number;
  min_content_items_per_module: number;
  max_content_items_per_module: number;
  min_scenes_per_content?: number;
  max_scenes_per_content?: number;
  min_duration_seconds?: number;
  max_duration_seconds?: number;
}

export interface CourseStructureAnalysis {
  /** False when nothing readable was found, or the analysis failed. Show `message` instead. */
  analysis_available: boolean;
  document_store_name: string;
  document_names: string[];
  document_summary: string;
  major_topic_count: number;
  supporting_topic_count: number;
  detail_topic_count: number;
  suggested: SuggestedStructureRange;
  delivery_type: CourseDeliveryType;
  /** True for video_presentation — the scene range applies. */
  includes_scenes: boolean;
  /** True for avatar_video — a per-content-item duration applies instead of scenes. */
  includes_duration: boolean;
  limits: DeliveryLimits;
  /** True when the documents hold more material than the largest course can teach. */
  detail_may_be_omitted: boolean;
  omitted_detail_note: string;
  message: string;
  /** When the cached analysis was produced. Absent when it has never run. */
  analyzed_at?: string;
}

export interface CourseStructureAnalysisResponse extends APIResponse<CourseStructureAnalysis> {}

// ==========================================
// WEBHOOK EVENT TYPES
// ==========================================

export type DocumentStoreWebhookEvent =
  | 'document_store.created'
  | 'document_store.updated'
  | 'document_store.deleted'
  | 'document.processing.started'
  | 'document.processing.completed'
  | 'document.processing.failed';

// ==========================================
// VALIDATION CONSTRAINTS
// ==========================================

export const DocumentStoreValidation = {
  name: {
    minLength: 1,
    maxLength: 100,
  },
  description: {
    maxLength: 500,
  },
  searchQuery: {
    minLength: 1,
    maxLength: 500,
  },
  list: {
    limit: {
      min: 1,
      max: 100,
      default: 10,
    },
    skip: {
      min: 0,
      default: 0,
    },
  },
} as const;

// ==========================================
// RATE LIMITS
// ==========================================

export const DocumentStoreRateLimits = {
  default: {
    requests: 100,
    window: '1m',
  },
  search: {
    requests: 20,
    window: '1m',
  },
  processing: {
    concurrent: 10,
  },
} as const;